"use client";
import NumberOptionList from "@/app/components/numberOptionList";
import PopoverError from "@/app/components/popoverError";
import AutoOrderMessageBox from "@/app/ec/product/[basePath]/auto-order/autoOrderMessageBox";
import AutoOrderPopover from "@/app/ec/product/[basePath]/auto-order/autoOrderPopover";
import checkAddToCart, { AddToCartStatus } from "@/app/ec/product/[basePath]/checkAddToCart";
import Button from "@/components/design-system/button";
import Dropdown from "@/components/design-system/dropdown";
import RadioButton from "@/components/design-system/radioButton";
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { ProductDisplayFormat } from "@/app/ec/product/productDisplayFormat";
import styles from "./callToAction.module.scss";

/**
 * Generates the call to action component on the PDP.  Includes logic for adding to auto order.
 * @param quantity The quantity selected by the user.
 * @param setQuantity A setState function tied to quantity that sets it to the new chosen quantity.
 */
const CallToAction: FunctionComponent<CallToActionProps> = ({
  quantity,
  setQuantity,
  maxInventoryAvailable,
  isAutoOrderEnabled,
  vendorOptions,
  subscriptionFutureDates,
  addToCartCallback,
  addToAutoOrderCallback,
  productBeingAddedToCart,
  vendorProductBeingAddedToCart,
  chosenAutoOrderLevel,
  setChosenAutoOrderLevel,
  chosenVendorKey,
  showExternalAddToCartError,
  disableExternalAddToCartErrorCallBack,
  justAddedToAutoOrder = false,
  format = "pdp",
}) => {
  const isReviewsFormat = format === "reviewsPage";
  const [showAddToCartError, setShowAddToCartError] = useState(false);
  const [subscribingToAutoOrder, setSubscribingToAutoOrder] = useState(false);
  const ref = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  useEffect(() => {
    if (showAddToCartError === true) {
      setShowAddToCartError(false);
    }
  }, [quantity, chosenVendorKey]);

  useEffect(() => {
    if (showExternalAddToCartError === true) {
      setShowAddToCartError(true);
      disableExternalAddToCartErrorCallBack(false);
    }
  }, [showExternalAddToCartError]);

  const elementIdentifier = "add-to-cart-cta ";
  const hideAutoOrderOptions = useMemo(() => {
    // Hide if we just added to auto order
    if (justAddedToAutoOrder) {
      return true;
    }

    const hasCurrentSubscription =
      subscriptionFutureDates !== undefined &&
      subscriptionFutureDates.length > 0 &&
      subscriptionFutureDates[0].SubscriptionId !== null;
    const canAutoOrder = isAutoOrderEnabled && !hasCurrentSubscription;

    return !canAutoOrder;
  }, [justAddedToAutoOrder, isAutoOrderEnabled, subscriptionFutureDates]);

  const handleSubmission = useCallback(async () => {
    if (productBeingAddedToCart === false) {
      if (chosenAutoOrderLevel === AutoOrderType.ONE_TIME.index) {
        if (
          checkAddToCart(
            quantity,
            vendorOptions[chosenVendorKey].minimumQuantityPurchasable,
            vendorOptions[chosenVendorKey].inventory,
          ) === AddToCartStatus.OK
        ) {
          addToCartCallback(vendorOptions[chosenVendorKey]);
        } else {
          setShowAddToCartError(true);
        }
      } else {
        setSubscribingToAutoOrder(true);
        const autoOrderLevel: AutoOrderFrequencyLevels = getSelectedAutoOrderType(chosenAutoOrderLevel)
          .frequenceId as AutoOrderFrequencyLevels;
        await addToAutoOrderCallback(
          {
            frequencyId: autoOrderLevel,
            quantity: quantity,
          },
          "Call to Action",
        );
        setSubscribingToAutoOrder(false);
      }
    }
  }, [
    productBeingAddedToCart,
    chosenAutoOrderLevel,
    quantity,
    vendorOptions,
    chosenVendorKey,
    checkAddToCart,
    setShowAddToCartError,
    setSubscribingToAutoOrder,
    addToAutoOrderCallback,
    getSelectedAutoOrderType,
  ]);

  const onSubmitHandler = useCallback(
    (e: React.FormEvent<HTMLElement>) => {
      e.preventDefault();

      handleSubmission();
    },
    [quantity, handleSubmission],
  );

  const onChangeCallback = useCallback(
    (event: any) => {
      console.log("quantity | onChange | " + event.target.value);
      setQuantity(parseInt(event.target.value));
    },
    [setQuantity],
  );

  const chosenProductBeingAddedToCart =
    vendorOptions[chosenVendorKey] && vendorProductBeingAddedToCart === vendorOptions[chosenVendorKey].vendorProductId;

  if (vendorOptions.length === 0) {
    return <div className={`${elementIdentifier}${isReviewsFormat ? ` ${styles.reviewsLayout}` : ""}`} />;
  }

  return (
    <Form
      className={`${elementIdentifier} pb-2 ${hideAutoOrderOptions ? "" : "auto-order-enabled"}${
        isReviewsFormat ? ` ${styles.reviewsLayout}` : ""
      }`}
      onSubmit={onSubmitHandler}
    >
      {/* Purchase Type Radio Buttons Box - Only show when auto order is enabled */}
      {!hideAutoOrderOptions && (
        <div
          className={`auto-order-options-box ${chosenAutoOrderLevel !== AutoOrderType.ONE_TIME.index ? "auto-order-selected" : ""}`}
        >
          <div className="purchase-type-radio-group">
            <RadioButton
              id="one-time-purchase"
              name="purchaseType"
              variant="purchaseType"
              label="One-time purchase"
              checked={chosenAutoOrderLevel === AutoOrderType.ONE_TIME.index}
              onChange={() => setChosenAutoOrderLevel(AutoOrderType.ONE_TIME.index)}
            />
            <RadioButton
              id="auto-order"
              name="purchaseType"
              variant="purchaseType"
              label="Auto Order"
              checked={chosenAutoOrderLevel !== AutoOrderType.ONE_TIME.index}
              onChange={() => setChosenAutoOrderLevel(AutoOrderType.EVERY_TWO_WEEKS.index)}
            />
          </div>

          {/* Auto Order Description Message - Only show when Auto Order is selected */}
          {chosenAutoOrderLevel !== AutoOrderType.ONE_TIME.index && (
            <div className={styles.autoOrderDescription}>
              <span>
                Easy repeat deliveries. We&apos;ll get you the best price, every time.{" "}
                <a href="https://www.net32.com/reorder-program" target="_self">
                  How it works
                </a>
              </span>
            </div>
          )}

          {/* Frequency Dropdown - Only show when Auto Order is selected */}
          {chosenAutoOrderLevel !== AutoOrderType.ONE_TIME.index && (
            <div className="auto-order-frequency-dropdown">
              <Dropdown
                placeholder="Auto-order frequency"
                value={chosenAutoOrderLevel.toString()}
                onChange={(event) => setChosenAutoOrderLevel(parseInt(event.target.value))}
              >
                {[AutoOrderType.EVERY_TWO_WEEKS, AutoOrderType.EVERY_MONTH, AutoOrderType.EVERY_TWO_MONTS].map(
                  (autoOrderType) => (
                    <option key={autoOrderType.index} value={autoOrderType.index}>
                      {autoOrderLevels[autoOrderType.index]}
                    </option>
                  ),
                )}
              </Dropdown>
            </div>
          )}

          {/* Auto Order Message - Only show when Auto Order is selected and subscription dates available */}
          {subscriptionFutureDates?.length > 1 && chosenAutoOrderLevel !== AutoOrderType.ONE_TIME.index && (
            <div className="auto-order-message-box">
              <AutoOrderMessageBox
                selectedAutoOrder={chosenAutoOrderLevel}
                subscriptionFutureDates={subscriptionFutureDates}
                autoOrderPopover={<AutoOrderPopover />}
              />
            </div>
          )}
        </div>
      )}

      <div className={isReviewsFormat ? styles.reviewsQuantitySelect : "quantity-select"}>
        <Dropdown placeholder="Quantity" value={quantity.toString()} onChange={(event) => onChangeCallback(event)}>
          <NumberOptionList key="foo" start={1} end={maxInventoryAvailable} />
        </Dropdown>
      </div>
      <Button
        id="add-to-cart-cta"
        type="submit"
        variant="primary"
        color="teal"
        showRightIcon={false}
        loading={chosenProductBeingAddedToCart || subscribingToAutoOrder}
        className={`add-to-cart-button${isReviewsFormat ? ` ${styles.reviewsButton}` : ""}`}
        ref={ref}
        text={chosenAutoOrderLevel === AutoOrderType.ONE_TIME.index ? "Add to Cart" : "Add to Auto Order"}
      />

      <PopoverError target={ref.current} show={showAddToCartError} closeCallback={() => setShowAddToCartError(false)}>
        {checkAddToCart(
          quantity,
          vendorOptions[chosenVendorKey].minimumQuantityPurchasable,
          vendorOptions[chosenVendorKey].inventory,
        ) === AddToCartStatus.MINIMUM_QUANTITY && (
          <>
            Minimum Order Quantity for this vendor is {vendorOptions[chosenVendorKey].minimumQuantityPurchasable}. Enter
            a larger quantity or select a different vendor.
          </>
        )}

        {checkAddToCart(
          quantity,
          vendorOptions[chosenVendorKey].minimumQuantityPurchasable,
          vendorOptions[chosenVendorKey].inventory,
        ) === AddToCartStatus.INSUFFICIENT_IVENTORY && (
          <>
            Quantity exceeds {vendorOptions[chosenVendorKey].vendorName}&apos;s stock of{" "}
            {vendorOptions[chosenVendorKey].inventory} item(s). Enter a smaller quantity or select a different vendor.
          </>
        )}
      </PopoverError>
    </Form>
  );
};

type CallToActionProps = {
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  maxInventoryAvailable: number;
  isAutoOrderEnabled: boolean;
  vendorOptions: VendorOptionState[];
  subscriptionFutureDates: SubscriptionFutureDatesForProduct[];
  addToCartCallback: (vendorProductToAdd: VendorOptionState) => void;
  addToAutoOrderCallback: (
    productToSubscribe: Pick<AddToAutoOrderProps, "frequencyId" | "quantity">,
    source: string,
  ) => Promise<void>;
  productBeingAddedToCart: boolean;
  vendorProductBeingAddedToCart: null | number;
  chosenVendorKey: number;
  chosenAutoOrderLevel: number;
  setChosenAutoOrderLevel: React.Dispatch<React.SetStateAction<number>>;
  showExternalAddToCartError: boolean;
  disableExternalAddToCartErrorCallBack: React.Dispatch<React.SetStateAction<boolean>>;
  justAddedToAutoOrder?: boolean;
  format?: ProductDisplayFormat;
};

export default React.memo(CallToAction);

export const autoOrderLevels = [
  "One-time purchase",
  "Delivery every 2 weeks",
  "Delivery every month",
  "Delivery every 2 months",
];

export const AutoOrderType = {
  ONE_TIME: {
    index: 0,
    indexServer: -1,
    description: "One time",
    frequenceId: "one-time",
    frequencyText: "",
  },
  EVERY_TWO_WEEKS: {
    index: 1,
    indexServer: 0,
    frequenceId: "weeks-2",
    description: "2 weeks",
    frequencyText: "on the 1st and 15th",
  },
  EVERY_MONTH: {
    index: 2,
    indexServer: 1,
    frequenceId: "months-1",
    description: "month",
    frequencyText: "on the 1st",
  },
  EVERY_TWO_MONTS: {
    index: 3,
    indexServer: 2,
    frequenceId: "months-2",
    description: "2 months",
    frequencyText: "on the 1st",
  },
};

export const getSelectedAutoOrderType = (selectedAutoOrder: number) => {
  switch (selectedAutoOrder) {
    case AutoOrderType.ONE_TIME.index: {
      return AutoOrderType.ONE_TIME;
    }
    case AutoOrderType.EVERY_TWO_WEEKS.index: {
      return AutoOrderType.EVERY_TWO_WEEKS;
    }
    case AutoOrderType.EVERY_MONTH.index: {
      return AutoOrderType.EVERY_MONTH;
    }
    case AutoOrderType.EVERY_TWO_MONTS.index: {
      return AutoOrderType.EVERY_TWO_MONTS;
    }
    default: {
      return AutoOrderType.ONE_TIME;
    }
  }
};

export type AutoOrderFrequencyLevels = "one-time" | "weeks-2" | "months-1" | "months-2";

export const getAutoOrderTypeByFrequenceId = (frequenceId: string) => {
  switch (frequenceId) {
    case AutoOrderType.ONE_TIME.frequenceId: {
      return AutoOrderType.ONE_TIME;
    }
    case AutoOrderType.EVERY_TWO_WEEKS.frequenceId: {
      return AutoOrderType.EVERY_TWO_WEEKS;
    }
    case AutoOrderType.EVERY_MONTH.frequenceId: {
      return AutoOrderType.EVERY_MONTH;
    }
    case AutoOrderType.EVERY_TWO_MONTS.frequenceId: {
      return AutoOrderType.EVERY_TWO_MONTS;
    }
    default: {
      return AutoOrderType.ONE_TIME;
    }
  }
};
export type AddToAutoOrderProps = {
  subscriberId: number | undefined;
  mpId: number;
  productTitle?: string;
  frequencyId: AutoOrderFrequencyLevels;
  quantity: number;
  licenseRequired: boolean;
};
